/* eslint-disable unicorn/no-object-as-default-parameter */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import axios from "axios";
import {
  getPostDataFromTypedPost,
  Post,
  PostApiInput,
  normalizePlatformType,
} from "../features/post";
import { normalizePostMediaShape } from "../features/post/utils";
import { extractApiError } from "../utils/get-error-message";
import type { PostCreationSource } from "../utils/post-creation-source";
import {
  markScheduleSubmitFailure,
  type ScheduleSubmitKind,
} from "../utils/schedule-submit-error";
import { skedApi } from "./_skedApi";
import {
  type ScheduleSubmitScope,
  withScheduleIdempotencyKey,
} from "./schedule-idempotency";
import { GeneratedSkedApiClient } from "./generated-client";
import type { paths } from "./generated/sked-api-openapi-types";
import type { components } from "./generated/schedugram-server-openapi-types";

const apiHashtagUrl = "https://hashtag.skedsocial.com/recommend";
const jsonUnsafeCharacterMap = {
  "<": String.raw`\u003C`,
  ">": String.raw`\u003E`,
  "&": String.raw`\u0026`,
} as const;

type GetPostByIdResponse =
  paths["/sked/posts/{postId}"]["get"]["responses"][200]["content"]["application/json"];
type GetPostByIdClientResponse = GetPostByIdResponse & {
  accountId?: string;
  accountIds?: string[];
};

type DraftApiInput = {
  _id: string;
  accountIds: string[];
  schedule: PostApiInput;
};

function getPostIdentifier(post: { _id?: string; id?: string }): string {
  const postId = post._id || post.id;
  if (!postId) {
    throw new Error("Post identifier is required");
  }
  return postId;
}

export function stringifyJsonBodyForWaf(data: unknown): string {
  const json = JSON.stringify(data);
  if (json === undefined) {
    throw new Error("Failed to serialize request body for WAF-safe transport");
  }

  return json.replaceAll(
    /[<>&]/g,
    (character) =>
      jsonUnsafeCharacterMap[character as keyof typeof jsonUnsafeCharacterMap]
  );
}

// WAF blocks JSON request bodies containing raw <, >, and & characters (SKE-3198).
// Unicode-escaping preserves the payload value after JSON.parse on the server.
// Ampersands are valid caption text, so they are escaped only in transport.
function createWafSafeJsonConfig(idempotencyKey?: string) {
  return {
    headers: {
      "Content-Type": "application/json",
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    },
    transformRequest: [(data: unknown) => stringifyJsonBodyForWaf(data)],
  };
}

export async function getPostById(postId: string) {
  const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();
  const result = await generatedSkedApiClient.client.GET(
    "/sked/posts/{postId}",
    {
      params: {
        path: { postId },
      },
    }
  );
  const data = (await generatedSkedApiClient.unwrap(
    result
  )) as GetPostByIdClientResponse;

  return {
    ...data,
    accountIds: data.accountIds || [data.accountId],
  };
}

export type GenerateAltTextPayload =
  | {
      assetId: string;
      mediaUrl?: never;
      /** Optional SEO/topic hint that biases generation (server-side prompt). */
      keywords?: string;
    }
  | {
      assetId?: never;
      mediaUrl: string;
      keywords?: string;
    };

export async function generateAltText(
  payload: GenerateAltTextPayload,
  options?: { signal?: AbortSignal }
): Promise<{ altText: string }> {
  const { data } = await skedApi.patch<{ altText: string }>(
    "/content-inbox/alt-text/generate",
    payload,
    { signal: options?.signal }
  );
  return data;
}

export async function createDraft(
  post: Post,
  creationSource?: PostCreationSource,
  scope?: ScheduleSubmitScope
) {
  const data = parseDataForCreatingPost(post, "draft", creationSource);
  return await postNewSchedules(data, scope, "draft_save");
}

export async function createScheduledPost(
  post: Post,
  creationSource?: PostCreationSource,
  scope?: ScheduleSubmitScope
) {
  const data = parseDataForCreatingPost(post, "schedule", creationSource);
  return await postNewSchedules(data, scope, "schedule");
}

export async function createQueuedPost(
  post: Post,
  creationSource?: PostCreationSource,
  scope?: ScheduleSubmitScope
) {
  const data = parseDataForCreatingPost(post, "queue", creationSource);
  return await postNewSchedules(data, scope, "schedule");
}

async function postNewSchedules(
  data: ReturnType<typeof parseDataForCreatingPost>,
  scope: ScheduleSubmitScope | undefined,
  kind: ScheduleSubmitKind
): Promise<string[]> {
  const rs = await withScheduleIdempotencyKey(
    scope,
    data,
    (idempotencyKey) =>
      axios.post(`/schedules`, data, createWafSafeJsonConfig(idempotencyKey)),
    kind
  );
  return rs.data as string[];
}

export type SchedulePayload = PostApiInput & { actionType: string };

export function schedulesValidate(data: {
  accountIds: string[];
  schedules: SchedulePayload[];
  timezone: string;
  uploader: string;
}): Promise<unknown> {
  return axios
    .post(`/schedules/validate`, data, createWafSafeJsonConfig())
    .catch((error) => {
      throw extractApiError(error);
    });
}
export async function editDraftValidation(post: Post): Promise<void> {
  const data = postToDraftApiInput(post);
  const postId = getPostIdentifier(post);
  await axios.put(
    `/drafts/${postId}/validate`,
    data,
    createWafSafeJsonConfig()
  );
}

export async function editDraft(
  post: Post,
  { validate }: { validate: boolean } = { validate: true }
): Promise<void> {
  const data = postToDraftApiInput(post);
  const postId = getPostIdentifier(post);
  validate && (await editDraftValidation(post));
  await axios.put(`/drafts/${postId}`, data, createWafSafeJsonConfig());
}

export async function moveDraftToSchedule(
  post: Post,
  scheduleAttemptId?: string
): Promise<string[][]> {
  try {
    const data = postToDraftApiInput(post);
    const postId = getPostIdentifier(post);
    const rs = await withScheduleIdempotencyKey(
      scheduleAttemptId
        ? { attemptId: scheduleAttemptId, part: postId }
        : undefined,
      data,
      (idempotencyKey) =>
        axios.put(
          `/drafts/${postId}/schedule`,
          data,
          createWafSafeJsonConfig(idempotencyKey)
        )
    );
    return rs.data;
  } catch (error) {
    throw markScheduleSubmitFailure(extractApiError(error), error);
  }
}

export async function moveQueueToDraft(postId: string) {
  try {
    const { data } = await axios.post(`/queue/${postId}/draft`);
    return data.newId;
  } catch (error) {
    throw extractApiError(error);
  }
}

export async function moveScheduleToDraft(postId: string): Promise<string> {
  try {
    const { data } = await axios.post(`/schedules/${postId}/draft`);
    return data.newId;
  } catch (error) {
    throw extractApiError(error);
  }
}

export async function moveQueueToSchedule(
  post: Post,
  scheduleAttemptId?: string
): Promise<{ data: string[] }> {
  const data = postToScheduledPostApiInput(post);
  const postId = getPostIdentifier(post);
  return await withScheduleIdempotencyKey(
    scheduleAttemptId
      ? { attemptId: scheduleAttemptId, part: postId }
      : undefined,
    data,
    (idempotencyKey) =>
      axios.put(
        `/queue/${postId}/schedule`,
        data,
        createWafSafeJsonConfig(idempotencyKey)
      )
  );
}

/** handled split */
export async function editScheduledPost(post: Post): Promise<string[]> {
  const data = postToScheduledPostApiInput(post);
  const postId = getPostIdentifier(post);
  const { data: ids } = await axios.put(
    `/schedules/${postId}`,
    data,
    createWafSafeJsonConfig()
  );
  return ids;
}

/** handled split */
export async function editQueuedPost(post: Post): Promise<string[]> {
  const data = postToScheduledPostApiInput(post);
  const postId = getPostIdentifier(post);
  const rs = await axios.put(
    `/queue/${postId}`,
    data,
    createWafSafeJsonConfig()
  );
  return rs.data;
}

export async function moveDraftToQueue(
  postId: string | string[],
  creationSource?: PostCreationSource
): Promise<string[]> {
  const rs = await axios.post(`/drafts/bulk/queue`, {
    postIds: Array.isArray(postId) ? postId : [postId],
    ...(creationSource ? { creationSource } : {}),
  });
  return rs.data;
}

export async function moveScheduledPostToQueue(
  postId: string | string[],
  creationSource?: PostCreationSource,
  postStatusKey?: string | null
) {
  const { data } = await axios.post(`/posts/bulk/queue`, {
    postIds: Array.isArray(postId) ? postId : [postId],
    ...(creationSource ? { creationSource } : {}),
    ...(postStatusKey !== undefined ? { postStatusKey } : {}),
  });

  return data;
}

function parseDataForCreatingPost(
  post: Post,
  state: string,
  creationSource?: PostCreationSource
) {
  const normalizedPost = normalizePostMediaShape(post);
  return {
    ...(creationSource ? { creationSource } : {}),
    accountIds: normalizedPost.accountIds,
    schedules: [
      {
        actionType: state,
        ...getPostDataFromTypedPost(normalizedPost),
      },
    ],
  };
}

function postToDraftApiInput(post: Post): DraftApiInput {
  const normalizedPost = normalizePostMediaShape(post);
  const postId = getPostIdentifier(normalizedPost);
  return <DraftApiInput>{
    _id: postId,
    // how this works previously??
    accountIds: normalizedPost.accountIds,
    schedule: getPostDataFromTypedPost(normalizedPost),
  };
}

function postToScheduledPostApiInput(post: Post) {
  const normalizedPost = normalizePostMediaShape(post);
  const postId = getPostIdentifier(normalizedPost);
  normalizedPost.platformType = normalizePlatformType(
    String(normalizedPost.platformType)
  );

  return {
    ...getPostDataFromTypedPost(normalizedPost),
    _id: postId,
    accountIds: normalizedPost.accountIds,
  };
}

export interface HashtagRecommendation {
  hashtag: string;
  metrics: {
    engagement: number;
    popularity: number;
    relevancy: Record<string, number>;
  };
}

export async function getRecommendedTags(
  data: {
    hashtags: string[];
  },
  signal?: AbortSignal
) {
  const response = await axios.request<{
    recommendation?: HashtagRecommendation[];
  }>({
    method: "post",
    url: apiHashtagUrl,
    data,
    signal,
  });
  return response.data.recommendation ?? [];
}

export async function markTagNegative(
  data: { hashtags: string[]; irrelevantHashtag: string },
  signal?: AbortSignal
) {
  const response = await axios.request<{
    recommendation?: HashtagRecommendation[];
  }>({
    method: "post",
    url: `${apiHashtagUrl}/negative`,
    data,
    signal,
  });
  return response.data.recommendation ?? [];
}

export async function markTagPositive(
  data: { hashtags: string[]; relevantHashtag: string },
  signal?: AbortSignal
): Promise<void> {
  await axios.request({
    method: "post",
    url: `${apiHashtagUrl}/positive`,
    data,
    signal,
  });
}

interface checkOverlappingPostsArguments {
  accountIds: string[];
  date: string;
  postId: string | undefined;
  /*
   * What the post IS. The server uses these to work out whether the publish
   * would reach a physical phone, which is the only case the 15-minute spacing
   * rule protects. Omitted for a caller that has no post to describe, which
   * keeps every account in scope.
   */
  postType?: string;
  storyType?: string;
  publishStoryManually?: boolean;
  isNotification?: boolean;
  hasStorySticker?: boolean;
  hasFirstComment?: boolean;
  mediaFileCount?: number;
}

/*
 * Derived, not restated. A hand-written copy of this shape had already fallen
 * two fields and a whole findings catalogue behind the endpoint.
 */
export type IgPreflightFinding = components["schemas"]["IgPreflightFindingDto"];
export type IgAccountPreflight = components["schemas"]["IgAccountPreflightDto"];
export type IgPreflightResult = components["schemas"]["IgPreflightResponse"];

/**
 * What the composer records when the preflight call itself fails, matching byte
 * for byte what the server answers when its own preflight throws. `unavailable`
 * says the findings mean nothing; an empty `accounts` list keeps every phone-era
 * limit in force, which is the safe direction.
 */
export const IG_PREFLIGHT_UNAVAILABLE: IgPreflightResult = {
  target: "FEED_IMAGE",
  accounts: [],
  postFindings: [],
  unavailable: true,
};

/**
 * The same unavailable answer, carrying one sentence the customer can act on.
 *
 * The route answers 403 for the WHOLE request when any selected account has no
 * usable Instagram token, naming the account to reconnect and returning no
 * findings for the healthy accounts either. Recording the sentence here is what
 * stops the composer swallowing it.
 */
export const igPreflightUnavailable = (
  message?: string
): IgPreflightResult => ({
  ...IG_PREFLIGHT_UNAVAILABLE,
  postFindings: message
    ? [{ severity: "warning", code: "preflight_unreachable", message }]
    : [],
});

/**
 * Asks the server how this draft would publish, per selected account.
 *
 * The composer cannot work this out for itself, and must not try: capability
 * needs the account's scopes and business-account id, which the accounts payload
 * does not carry, and re-deriving the post-expressibility rules here would put a
 * second copy of them in the product. So the whole post goes to the server and
 * the server answers.
 */
export async function preflightInstagramPost({
  accountIds,
  post,
}: {
  accountIds: string[];
  post: unknown;
}): Promise<IgPreflightResult> {
  const { data } = await axios.post("/instagram-lookups/preflight", post, {
    params: { accountIds: accountIds.join(",") },
  });

  return data;
}

/*
 * Derived, not restated, for the same reason the preflight types above are.
 */
export type IgLocation = components["schemas"]["IgLocationDto"];
export type IgLocationSearchResult =
  components["schemas"]["IgLocationSearchResponse"];

/**
 * SKE-4538. Searches the places an Instagram post can actually be tagged with.
 *
 * The legacy `/location/query` returns any Facebook Page the search matched.
 * This one returns only Pages carrying location data, which is the difference
 * that matters: a Page id without a location fails the whole publish container
 * at Meta, long after the customer picked it out of a list.
 *
 * `accountIds` is a comma-separated QUERY parameter. Sending accounts in a body
 * answers "Account not found". The route tries the accounts in order and answers
 * from the first one Instagram accepts, because a `location_id` means the same
 * place on every account. Accounts it skipped come back in `rejectedAccounts`,
 * each with a `reason`. It answers 403 only when none of them can search, and
 * callers treat a throw as "use the legacy route instead".
 */
export async function searchInstagramLocations({
  accountIds,
  query,
  after,
}: {
  accountIds: string[];
  query: string;
  after?: string;
}): Promise<IgLocationSearchResult> {
  const { data } = await axios.get("/instagram-lookups/locations", {
    params: { accountIds: accountIds.join(","), q: query, after },
  });

  return data;
}

export type IgLocationValidationResult =
  components["schemas"]["IgLocationValidationResponse"];

// SKE-5363. `unchecked` ids are unknown, not valid.
export async function validateInstagramLocations({
  accountIds,
  ids,
}: {
  accountIds: string[];
  ids: string[];
}): Promise<IgLocationValidationResult> {
  const { data } = await axios.get("/instagram-lookups/locations/validate", {
    params: { accountIds: accountIds.join(","), ids: ids.join(",") },
  });

  return data;
}

/*
 * Derived, not restated, for the same reason the preflight types above are.
 */
export type IgCatalogProduct = components["schemas"]["IgCatalogProductDto"];
export type IgProductPickerResult =
  components["schemas"]["IgProductPickerResponse"];

/**
 * SKE-4543. Searches the Instagram Shop catalog the selected accounts share.
 *
 * The whole selection goes in one request on purpose. A `product_id` is
 * catalog-scoped and the composer stores one set of product tags for every
 * selected account, so a per-account answer would let the picker offer ids that
 * mean something different on the accounts it did not ask about. The route
 * answers `catalog_mismatch` rather than guessing.
 *
 * The result is a union flattened onto `status`, and `status` is the only field
 * to branch on: `ok` carries `products`, `not_eligible` and `no_catalog` carry
 * the accounts to blame, `catalog_mismatch` carries the catalog ids. It throws
 * 403 for the WHOLE request when any account passed has no usable Instagram
 * token, carrying a reconnect sentence that names the account.
 */
export async function searchInstagramCatalogProducts({
  accountIds,
  query,
  after,
}: {
  accountIds: string[];
  query?: string;
  after?: string;
}): Promise<IgProductPickerResult> {
  const { data } = await axios.get("/instagram-shopping/products", {
    params: { accountIds: accountIds.join(","), q: query || undefined, after },
  });

  return data;
}

/**
 * The products route's 409 for a cursor issued before the catalog last synced.
 * The caller restarts the search from page one rather than showing an error.
 */
export function isStaleCatalogCursorError(error: unknown): boolean {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: Partial<components["schemas"]["IgShoppingAccessErrorDto"]>;
      };
    }
  )?.response;
  return response?.status === 409 && response.data?.code === "stale_cursor";
}

export type IgCatalogStatusResult =
  components["schemas"]["IgCatalogStatusResponse"];
export type IgCatalogAccountSyncStatus =
  components["schemas"]["IgCatalogSyncAccountStatusDto"];
export type IgCatalogRefreshResult =
  components["schemas"]["IgCatalogRefreshResponse"];

/** SKE-5090. The stored catalog sync for each selected account. */
export async function getInstagramCatalogStatus(
  accountIds: string[]
): Promise<IgCatalogStatusResult> {
  const { data } = await axios.get("/instagram-shopping/catalog-status", {
    params: { accountIds: accountIds.join(",") },
  });

  return data;
}

/**
 * SKE-5090. Asks for one account's catalog to be synced again.
 *
 * The handler's answers carry a `status`, including the 4xx and 502 ones, so
 * those are returned rather than thrown and the caller branches on `status`
 * alone. The account check in front of it answers 403 and 404 with
 * `{ error, message }` and no `status`; those throw, as does anything else (a
 * network failure, an undocumented shape).
 */
export async function refreshInstagramCatalog(
  accountId: string
): Promise<IgCatalogRefreshResult> {
  try {
    const { data } = await axios.post("/instagram-shopping/refresh", null, {
      params: { accountIds: accountId },
    });
    return data;
  } catch (error) {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    if (
      data &&
      typeof data === "object" &&
      typeof (data as { status?: unknown }).status === "string"
    ) {
      return data as IgCatalogRefreshResult;
    }
    throw error;
  }
}

/*
 * Derived, not restated, for the same reason the preflight types above are.
 */
export type IgAudioTrack = components["schemas"]["IgAudioTrackDto"];
export type IgAudioType = components["schemas"]["IgAudioTypeDto"];
export type IgAudioSearchResult =
  components["schemas"]["IgAudioSearchResponse"];
export type IgLookupRejectedAccount =
  components["schemas"]["IgLookupRejectedAccountDto"];
export type IgLookupErrorResponse =
  components["schemas"]["IgLookupErrorResponse"];

/**
 * SKE-4542. Searches the audio Meta has licensed for third-party publishing.
 *
 * `audioType` is REQUIRED and there is no value meaning both, so a picker
 * offering music and original sounds together makes two calls. An absent `query`
 * asks for what Meta considers trending rather than for nothing.
 *
 * `accountIds` is a comma-separated QUERY parameter. The route tries the
 * accounts in order and answers from the first one Instagram accepts, because an
 * `audio_id` means the same track on every account. Accounts it could not use
 * come back in `rejectedAccounts`; when none worked it answers 403 carrying the
 * same list. `after` belongs to `servedAccountId`, so a next page is requested
 * from that account alone.
 */
export async function searchInstagramAudio({
  accountIds,
  audioType,
  query,
  after,
}: {
  accountIds: string[];
  audioType: IgAudioType;
  query?: string;
  after?: string;
}): Promise<IgAudioSearchResult> {
  const { data } = await axios.get("/instagram-lookups/audio", {
    params: {
      accountIds: accountIds.join(","),
      audioType,
      q: query || undefined,
      after,
    },
  });

  return data;
}

export async function checkOverlappingPosts({
  accountIds,
  date,
  postId,
  ...trackFacts
}: checkOverlappingPostsArguments): Promise<unknown> {
  const { data } = await axios.get("/instagram/checktime", {
    params: { accountIds, date, postId, ...trackFacts },
  });

  return data;
}
